import type { TemplateType } from "@/components/offer/offerDocumentTypes";
import { buildValidityAssumption } from "@/lib/offerDocumentDefaults";

export const DEFAULT_ASSUMPTIONS = [
  "Ceny obowiązują przy zakresie prac zgodnym z ofertą",
  "Prace dodatkowe wymagają pisemnej akceptacji i osobnej wyceny",
  buildValidityAssumption(),
  "Materiały dostarczone przez klienta muszą być na miejscu przed rozpoczęciem prac",
  "Wykonawca zapewnia porządek po zakończeniu każdego dnia roboczego",
] as const;

export const EXCLUSION_OPTIONS = [
  "Wykonawca nie odpowiada za wady materiałów dostarczonych przez klienta",
  "Wykonawca nie odpowiada za wady podłoża niewidoczne przed rozpoczęciem prac",
  "Prace instalacyjne (elektryka, hydraulika) wykonywane przez uprawnionych podwykonawców",
  "Wykonawca nie odpowiada za opóźnienia wynikające z niedostępności materiałów dostarczanych przez klienta",
] as const;

/** @deprecated Wyłączenia scalone z założeniami — tylko do migracji starych szkiców. */
export const DEFAULT_EXCLUSIONS: string[] = [EXCLUSION_OPTIONS[0], EXCLUSION_OPTIONS[1]];

export type AssumptionRow = {
  id: string;
  text: string;
  checked: boolean;
};

const ASSUMPTION_DEFAULTS_CHECKED_COUNT = 3;

/** Wersja domyślnych checkboxów — bump wymusza odświeżenie zapisu lokalnego. */
export const ASSUMPTION_DEFAULTS_VERSION = 2;
const ASSUMPTION_DEFAULTS_LS_KEY = "builder-mvp-assumption-defaults-v";
const LEGACY_MVP_DRAFTS_LS_KEY = "builder-mvp-drafts-v1";

export function createDefaultAssumptionRows(): AssumptionRow[] {
  return [
    ...DEFAULT_ASSUMPTIONS.map((text, index) => ({
      id: `assumption-${index}`,
      text,
      checked: index < ASSUMPTION_DEFAULTS_CHECKED_COUNT,
    })),
    ...([0, 1] as const).map((index) => ({
      id: `exclusion-${index}`,
      text: EXCLUSION_OPTIONS[index],
      checked: false,
    })),
  ];
}

/** Jednorazowo czyści zapisane założenia w localStorage, aby wczytać nowe domyślne checkboxy. */
export function clearLegacyAssumptionLocalStorage(): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (localStorage.getItem(ASSUMPTION_DEFAULTS_LS_KEY) === String(ASSUMPTION_DEFAULTS_VERSION)) {
      return;
    }

    const raw = localStorage.getItem(LEGACY_MVP_DRAFTS_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        drafts?: Array<{ bundle?: Record<string, unknown> }>;
      };
      if (Array.isArray(parsed.drafts)) {
        let changed = false;
        for (const draft of parsed.drafts) {
          const bundle = draft.bundle;
          if (!bundle || typeof bundle !== "object") continue;
          if ("offerAssumptions" in bundle) {
            delete bundle.offerAssumptions;
            changed = true;
          }
          if ("exclusions" in bundle) {
            delete bundle.exclusions;
            changed = true;
          }
        }
        if (changed) {
          localStorage.setItem(LEGACY_MVP_DRAFTS_LS_KEY, JSON.stringify(parsed));
        }
      }
    }

    localStorage.setItem(ASSUMPTION_DEFAULTS_LS_KEY, String(ASSUMPTION_DEFAULTS_VERSION));
  } catch {
    localStorage.setItem(ASSUMPTION_DEFAULTS_LS_KEY, String(ASSUMPTION_DEFAULTS_VERSION));
  }
}

export function defaultCheckedAssumptionTexts(): string[] {
  return createDefaultAssumptionRows()
    .filter((row) => row.checked)
    .map((row) => row.text);
}

const PLACEHOLDER_EXACT = new Set(
  [
    "no nie wiem",
    "nowe założenie",
    "lorem ipsum",
    "test",
    "placeholder",
    "jakie założenia przykładowo tu wpisać?",
  ].map((s) => s.toLowerCase())
);

export function isStaleAssumptionText(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  if (PLACEHOLDER_EXACT.has(lower)) return true;
  if (lower.includes("przykładowo")) return true;
  if (lower.includes("wpisać") || lower.includes("wpisac")) return true;
  if (/jakie\s+założenia/i.test(lower)) return true;
  return false;
}

export function savedAssumptionDataIsStale(
  offerAssumptions?: string[],
  exclusions?: string[]
): boolean {
  const all = [...(offerAssumptions ?? []), ...(exclusions ?? [])]
    .map((item) => item.trim())
    .filter(Boolean);
  if (all.length === 0) return false;
  return all.some(isStaleAssumptionText);
}

export function resolveInitialAssumptionRows(
  offerAssumptions?: string[],
  exclusions?: string[]
): AssumptionRow[] {
  clearLegacyAssumptionLocalStorage();

  if (savedAssumptionDataIsStale(offerAssumptions, exclusions)) {
    return createDefaultAssumptionRows();
  }

  const savedChecked = new Set(
    [...(offerAssumptions ?? []), ...(exclusions ?? [])]
      .map((item) => item.trim())
      .filter((item) => item && !isStaleAssumptionText(item))
  );

  if (savedChecked.size === 0) {
    return createDefaultAssumptionRows();
  }

  const defaultRows = createDefaultAssumptionRows();
  const defaultTexts = new Set(defaultRows.map((row) => row.text));

  const rows = defaultRows.map((row) => ({
    ...row,
    checked: savedChecked.has(row.text),
  }));

  const extras = [...savedChecked]
    .filter((text) => !defaultTexts.has(text))
    .map((text) => ({
      id: crypto.randomUUID(),
      text,
      checked: true,
    }));

  return extras.length > 0 ? [...rows, ...extras] : rows;
}

export const DEFAULT_PAYMENT_TEXT =
  "Płatność przelewem w ciągu 14 dni od daty wystawienia faktury.";

export const DEFAULT_WARRANTY_TEXT = "Gwarancja: 24 miesiące od dnia odbioru prac.";

export const WARRANTY_PRESET_TEXT: Record<12 | 24 | 36, string> = {
  12: "Gwarancja: 12 miesięcy od dnia odbioru prac.",
  24: "Gwarancja: 24 miesiące od dnia odbioru prac.",
  36: "Gwarancja: 36 miesięcy od dnia odbioru prac.",
};

export type Step3SectionIncludes = {
  clientDetails: boolean;
  materials: boolean;
  warranty: boolean;
  payment: boolean;
  assumptions: boolean;
  timeline: boolean;
  scopeSummary: boolean;
  includedExcluded: boolean;
  aboutUs: boolean;
  schedule: boolean;
  process: boolean;
  projectManagement: boolean;
  /** @deprecated Scalone z `assumptions`. */
  exclusions?: boolean;
};

export const DEFAULT_STEP3_SECTION_INCLUDES: Step3SectionIncludes = {
  clientDetails: true,
  materials: true,
  warranty: true,
  payment: true,
  assumptions: true,
  timeline: true,
  scopeSummary: true,
  includedExcluded: true,
  aboutUs: true,
  schedule: true,
  process: true,
  projectManagement: true,
};

function defaultTimelineIncluded(templateType?: TemplateType): boolean {
  return templateType === "explained" || templateType === "premium";
}

export function normalizeStep3SectionIncludes(
  raw: Partial<Step3SectionIncludes> | undefined,
  templateType?: TemplateType
): Step3SectionIncludes {
  const assumptionsOn = raw?.assumptions !== false && raw?.exclusions !== false;
  const timeline =
    raw?.timeline !== undefined ? raw.timeline !== false : defaultTimelineIncluded(templateType);
  return {
    clientDetails: raw?.clientDetails !== false,
    materials: raw?.materials !== false,
    warranty: raw?.warranty !== false,
    payment: raw?.payment !== false,
    assumptions: assumptionsOn,
    timeline,
    scopeSummary: raw?.scopeSummary !== false,
    includedExcluded: raw?.includedExcluded !== false,
    aboutUs: raw?.aboutUs !== false,
    schedule: raw?.schedule !== false,
    process: raw?.process !== false,
    projectManagement: raw?.projectManagement !== false,
  };
}
